import { createStore } from '@shared/utils/state/state-factory'
import { REGIONS } from 'v2/configs/constants'
import type { RV2ComplianceApplication } from 'v2/pages/settings/number-compliance-v2/types-v2'
import { mapV2ApplicationDocumentsToForm } from 'v2/pages/settings/number-compliance-v2/utils-v2'
import { getActiveRegion } from 'v2/shared/services/create-service'
import { NUMBER_SERIES_ALIAS_PREFIX } from './constants'
import {
  deriveGstRegisteredFromDocuments,
  isGstDocument,
  resolveRegistrationDocumentType,
} from './gst-documents'
import type {
  CallsPlacedType,
  ComplianceFieldValue,
  ComplianceFormValues,
  ComplianceFormVariant,
  ComplianceSheetMode,
  NumberSeries,
  RComplianceRequirement,
  RegistrationDocumentType,
} from './types'
import {
  generateCafUpdateAlias,
  getDocumentFieldKey,
  getDocumentFileKey,
  getNumberSeriesFromAlias,
  stripNumberSeriesPrefix,
} from './utils'

// State interface
interface ComplianceFormState {
  mode: ComplianceSheetMode
  variant: ComplianceFormVariant
  application: RV2ComplianceApplication | null
  requirements: RComplianceRequirement | null
  requirementsError: string | null
  formValues: ComplianceFormValues
  errors: Record<string, string>
  isLoading: boolean
  isSubmitting: boolean
}

// Actions interface
interface ComplianceFormActions {
  // Initialization
  initialize: (
    mode: ComplianceSheetMode,
    application?: RV2ComplianceApplication,
    variant?: ComplianceFormVariant,
  ) => void
  setRequirements: (requirements: RComplianceRequirement) => void
  setRequirementsError: (error: string | null) => void

  // Base field setters
  setAlias: (alias: string) => void
  setCountry: (country: string) => void
  setNumberType: (numberType: 'local' | 'tollfree') => void
  setNumberSeries: (series: NumberSeries) => void
  setCallsPlacedType: (callsPlacedType: CallsPlacedType) => void
  setGstRegistered: (gstRegistered: boolean) => void
  setRegistrationDocumentType: (registrationDocumentType: RegistrationDocumentType) => void
  resetRegistrationDocumentType: () => void

  // Document field actions
  setFieldValue: (documentTypeId: string, fieldName: string, value: ComplianceFieldValue) => void
  setFile: (documentTypeId: string, file: File) => void
  removeFile: (documentTypeId: string) => void
  setExtractedDetails: (documentTypeId: string, details: Record<string, string>) => void

  // Error handling
  setErrors: (errors: Record<string, string>) => void
  clearError: (key: string) => void
  clearFieldError: (documentTypeId: string, fieldName: string) => void

  // Status
  setLoading: (loading: boolean) => void
  setSubmitting: (submitting: boolean) => void

  // Reset
  reset: () => void
}

const defaultFormValues: ComplianceFormValues = {
  alias: '',
  country_iso2: '',
  number_type: 'local',
  numberSeries: 'other',
  callsPlacedType: null,
  gstRegistered: null,
  registrationDocumentType: null,
  uploadedFiles: {},
  documentFieldValues: {},
  existingDocuments: {},
  extractedDetails: {},
}

const initialState: ComplianceFormState = {
  mode: 'create',
  variant: 'kyc',
  application: null,
  requirements: null,
  requirementsError: null,
  formValues: defaultFormValues,
  errors: {},
  isLoading: false,
  isSubmitting: false,
}

export const useComplianceFormStore = createStore<ComplianceFormState & ComplianceFormActions>(
  'complianceForm',
  (set, get) => ({
    ...initialState,

    initialize: (mode, application, variant = 'kyc') => {
      // The session's region on the hosted page, else the signed-in account's.
      const userRegion = getActiveRegion()
      const isIndiaRegion = userRegion === REGIONS.IN

      if (mode === 'create') {
        // A create can be SEEDED from an existing application: correcting a
        // rejection walks the same flow again with the customer's own name for it
        // already filled in and editable. ONLY the name (and the series it
        // encodes) carries over — every other answer is asked again, because a
        // rejection means one of them was wrong. `application` stays null: this is
        // a new record, not an edit of that one.
        const seedIsIndian = application?.country_iso === 'IN'
        set({
          mode,
          variant,
          application: null,
          requirements: null,
          requirementsError: null,
          formValues: {
            ...defaultFormValues,
            ...(application && {
              alias: seedIsIndian
                ? stripNumberSeriesPrefix(application.alias || '')
                : application.alias || '',
              numberSeries: seedIsIndian
                ? getNumberSeriesFromAlias(application.alias || '')
                : 'other',
            }),
            // The CAF update has no Name field, so the alias is filled here.
            ...(variant === 'caf-update' && { alias: generateCafUpdateAlias() }),
            // Only default to 'IN' for India region users
            country_iso2: isIndiaRegion ? 'IN' : '',
          },
          errors: {},
          isLoading: false,
          isSubmitting: false,
        })
      } else if (application) {
        // The 140/160 series is an India-only concept encoded in the alias prefix.
        // For India apps, store the alias without the prefix so it renders as an
        // input adornment and is re-added on submit; for other countries the alias
        // is kept verbatim (a non-IN alias starting with "140-"/"160-" is coincidental).
        const isIndiaApplication = application.country_iso === 'IN'
        const numberSeries = isIndiaApplication
          ? getNumberSeriesFromAlias(application.alias || '')
          : 'other'
        set({
          mode,
          variant,
          application,
          requirements: null,
          requirementsError: null,
          formValues: {
            ...defaultFormValues,
            alias: isIndiaApplication
              ? stripNumberSeriesPrefix(application.alias || '')
              : application.alias || '',
            country_iso2: application.country_iso || '',
            number_type: (application.number_type as 'local' | 'tollfree') || 'local',
            numberSeries,
          },
          errors: {},
          isLoading: false,
          isSubmitting: false,
        })
      }
    },

    setRequirements: (requirements) => {
      const { mode, application } = get()

      if ((mode === 'edit' || mode === 'view') && application) {
        const { documentFieldValues, existingDocuments } = mapV2ApplicationDocumentsToForm(
          application,
          requirements.document_fields,
        )
        // Nothing records the GST answer on a submitted application, so infer it
        // from the documents it actually carries (backfill only — there is no
        // stored value to override).
        // meta_information is what the backend read off each document.
        const extractedDetails: Record<string, Record<string, string>> = {}
        for (const appDoc of application.documents ?? []) {
          if (appDoc.meta_information) {
            extractedDetails[appDoc.document_type_id] = appDoc.meta_information
          }
        }
        const storedDocumentNames = requirements.document_fields
          .filter((docField) => existingDocuments[docField.document_type_id])
          .map((docField) => docField.document_name)
        // Which registration document was filed, so reopening restores the
        // answer instead of asking for it again.
        const registrationDocument = (application.documents ?? []).find(
          (appDoc) => !isGstDocument(appDoc.document_name),
        )
        const registrationDocumentType = registrationDocument
          ? resolveRegistrationDocumentType(
              registrationDocument.registration_document_type,
              registrationDocument.meta_information,
            )
          : null
        set((state) => {
          state.requirements = requirements
          state.requirementsError = null
          state.formValues.documentFieldValues = documentFieldValues
          state.formValues.existingDocuments = existingDocuments
          state.formValues.uploadedFiles = {}
          state.formValues.gstRegistered = deriveGstRegisteredFromDocuments(
            storedDocumentNames,
            registrationDocumentType,
          )
          state.formValues.registrationDocumentType = registrationDocumentType
          state.formValues.extractedDetails = extractedDetails
        })
      } else {
        set((state) => {
          state.requirements = requirements
          state.requirementsError = null
          state.formValues.documentFieldValues = {}
          state.formValues.existingDocuments = {}
          state.formValues.uploadedFiles = {}
          state.formValues.extractedDetails = {}
        })
      }
    },

    setRequirementsError: (error) => {
      set((state) => {
        state.requirementsError = error
        state.requirements = null
      })
    },

    setAlias: (alias) => {
      set((state) => {
        state.formValues.alias = alias
        delete state.errors.alias
      })
    },

    setCountry: (country) => {
      set((state) => {
        state.formValues.country_iso2 = country
        state.requirementsError = null
        delete state.errors.country_iso2
      })
    },

    setNumberType: (numberType) => {
      set((state) => {
        state.formValues.number_type = numberType
        state.requirementsError = null
      })
    },

    setNumberSeries: (series) => {
      set((state) => {
        state.formValues.numberSeries = series
        // 160 defaults to self-managed; other series don't use this field.
        state.formValues.callsPlacedType = series === '160' ? 'self' : null
        delete state.errors.callsPlacedType
      })
    },

    setCallsPlacedType: (callsPlacedType) => {
      set((state) => {
        state.formValues.callsPlacedType = callsPlacedType
        delete state.errors.callsPlacedType
      })
    },

    // Uploaded files are deliberately kept: switching the answer back and forth
    // shouldn't discard a file, and the hidden slot is filtered out of both
    // validation and the payload by selectVisibleDocumentFields.
    setGstRegistered: (gstRegistered) => {
      set((state) => {
        state.formValues.gstRegistered = gstRegistered
        // The follow-up only exists on the no branch; a stale answer would
        // otherwise decide which example a GST applicant is shown.
        if (gstRegistered) state.formValues.registrationDocumentType = null
        delete state.errors.gstRegistered
      })
    },

    setRegistrationDocumentType: (registrationDocumentType) => {
      set((state) => {
        state.formValues.registrationDocumentType = registrationDocumentType
        delete state.errors.registrationDocumentType
      })
    },

    // For a 'none' picked while useKycApplicationCount() was still unresolved:
    // once the count resolves and withdraws the option, a stale 'none' hides the
    // document section with submission blocked and nothing on screen saying why.
    resetRegistrationDocumentType: () => {
      set((state) => {
        state.formValues.registrationDocumentType = null
        delete state.errors.registrationDocumentType
      })
    },

    setFieldValue: (documentTypeId, fieldName, value) => {
      set((state) => {
        if (!state.formValues.documentFieldValues[documentTypeId]) {
          state.formValues.documentFieldValues[documentTypeId] = {}
        }
        state.formValues.documentFieldValues[documentTypeId][fieldName] = value
        // Clear error for this field
        delete state.errors[getDocumentFieldKey(documentTypeId, fieldName)]
      })
    },

    setFile: (documentTypeId, file) => {
      set((state) => {
        state.formValues.uploadedFiles[documentTypeId] = file
        // Whatever was read off the previous file no longer describes this one.
        delete state.formValues.extractedDetails[documentTypeId]
        delete state.errors[getDocumentFileKey(documentTypeId)]
      })
    },

    setExtractedDetails: (documentTypeId, details) => {
      set((state) => {
        state.formValues.extractedDetails[documentTypeId] = details
      })
    },

    removeFile: (documentTypeId) => {
      set((state) => {
        delete state.formValues.uploadedFiles[documentTypeId]
        delete state.formValues.existingDocuments[documentTypeId]
        delete state.formValues.extractedDetails[documentTypeId]
      })
    },

    setErrors: (errors) => set({ errors }),

    clearError: (key) => {
      set((state) => {
        delete state.errors[key]
      })
    },

    clearFieldError: (documentTypeId, fieldName) => {
      set((state) => {
        delete state.errors[getDocumentFieldKey(documentTypeId, fieldName)]
      })
    },

    setLoading: (loading) => set({ isLoading: loading }),

    setSubmitting: (submitting) => set({ isSubmitting: submitting }),

    reset: () => set({ ...initialState }),
  }),
)

// Selectors for derived state
export const selectIsReadOnly = (state: ComplianceFormState) => state.mode === 'view'

export const selectIsEditMode = (state: ComplianceFormState) => state.mode === 'edit'

export const selectIsCreateMode = (state: ComplianceFormState) => state.mode === 'create'

export const selectIsCafUpdate = (state: ComplianceFormState) => state.variant === 'caf-update'

export const selectIsIndiaCountry = (state: ComplianceFormState) =>
  state.formValues.country_iso2 === 'IN'

export const selectAliasPrefix = (state: ComplianceFormState) =>
  NUMBER_SERIES_ALIAS_PREFIX[state.formValues.numberSeries]

export const selectFieldValue = (
  state: ComplianceFormState,
  documentTypeId: string,
  fieldName: string,
): ComplianceFieldValue => state.formValues.documentFieldValues[documentTypeId]?.[fieldName] ?? null

export const selectFieldError = (
  state: ComplianceFormState,
  documentTypeId: string,
  fieldName: string,
): string | undefined => state.errors[getDocumentFieldKey(documentTypeId, fieldName)]

export type FileInfo =
  | { type: 'new'; file: File }
  | { type: 'existing'; v4_document_id: string; file_name: string; download_url?: string }
  | null

export const selectFileInfo = (state: ComplianceFormState, documentTypeId: string): FileInfo => {
  const uploadedFile = state.formValues.uploadedFiles[documentTypeId]
  const existingDoc = state.formValues.existingDocuments[documentTypeId]

  if (uploadedFile) {
    return { type: 'new', file: uploadedFile }
  }
  if (existingDoc) {
    return { type: 'existing', ...existingDoc }
  }
  return null
}

export const selectFileError = (
  state: ComplianceFormState,
  documentTypeId: string,
): string | undefined => state.errors[getDocumentFileKey(documentTypeId)]
